import { createClerkSupabaseClient } from '../../lib/supabase.js';

class DetailedAnalyticsService {
  constructor(session) {
    this.supabase = createClerkSupabaseClient(session);
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
  }

  // Método genérico para cache
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Obtener datos de sesiones
  async getSessionsData(companyId, dateRange = 30) {
    const cacheKey = `sessions_${companyId}_${dateRange}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: sessions, error } = await this.supabase
        .from('sesiones')
        .select(`
          id,
          created_at,
          canal,
          user_agent,
          datos_simulacion,
          datos_dps,
          expires_at
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Obtener cotizaciones relacionadas para calcular conversiones
      const { data: quotations } = await this.supabase
        .from('cotizaciones')
        .select('sesion_id, created_at, estado')
        .in('sesion_id', sessions.map(s => s.id));

      const processedData = this.processSessionsData(sessions, quotations || []);
      this.setCachedData(cacheKey, processedData);
      return processedData;

    } catch (error) {
      console.error('Error fetching sessions data:', error);
      return this.getDefaultSessionsData();
    }
  }

  // Obtener datos de cotizaciones
  async getQuotationsData(companyId, dateRange = 30) {
    const cacheKey = `quotations_${companyId}_${dateRange}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: quotations, error } = await this.supabase
        .from('cotizaciones')
        .select(`
          id,
          created_at,
          estado,
          precio_total_uf,
          plan_id,
          producto_id,
          sesion_id,
          planes:plan_id(nombre),
          producto:producto_id(nombre)
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedData = this.processQuotationsData(quotations || []);
      this.setCachedData(cacheKey, processedData);
      return processedData;

    } catch (error) {
      console.error('Error fetching quotations data:', error);
      return this.getDefaultQuotationsData();
    }
  }

  // Obtener datos de contratos
  async getContractsData(companyId, dateRange = 30) {
    const cacheKey = `contracts_${companyId}_${dateRange}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: contracts, error } = await this.supabase
        .from('contrataciones')
        .select(`
          id,
          created_at,
          estado,
          fecha_firma,
          cotizacion_id,
          rut_titular,
          nombre_completo,
          mail,
          cotizaciones:cotizacion_id(precio_total_uf, planes(nombre), producto(nombre))
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedData = this.processContractsData(contracts || []);
      this.setCachedData(cacheKey, processedData);
      return processedData;

    } catch (error) {
      console.error('Error fetching contracts data:', error);
      return this.getDefaultContractsData();
    }
  }

  // Obtener datos de certificados
  async getCertificatesData(companyId, dateRange = 30) {
    const cacheKey = `certificates_${companyId}_${dateRange}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: certificates, error } = await this.supabase
        .from('certificados')
        .select(`
          id,
          created_at,
          estado,
          fecha_emision,
          fecha_vencimiento,
          numero_certificado,
          orden_pago_id,
          archivo_id
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedData = this.processCertificatesData(certificates || []);
      this.setCachedData(cacheKey, processedData);
      return processedData;

    } catch (error) {
      console.error('Error fetching certificates data:', error);
      return this.getDefaultCertificatesData();
    }
  }

  // Obtener datos de suscripciones
  async getSubscriptionsData(companyId, dateRange = 30) {
    const cacheKey = `subscriptions_${companyId}_${dateRange}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: subscriptions, error } = await this.supabase
        .from('subscriptions')
        .select(`
          id,
          created_at,
          status,
          plan_name,
          amount,
          currency,
          billing_frequency,
          start_date,
          end_date,
          next_billing_date,
          auto_renew
        `)
        .eq('company_id', companyId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processedData = this.processSubscriptionsData(subscriptions || []);
      this.setCachedData(cacheKey, processedData);
      return processedData;

    } catch (error) {
      console.error('Error fetching subscriptions data:', error);
      return this.getDefaultSubscriptionsData();
    }
  }

  // Procesadores de datos
  processSessionsData(sessions, quotations) {
    const quotationsBySession = quotations.reduce((acc, q) => {
      if (!acc[q.sesion_id]) acc[q.sesion_id] = [];
      acc[q.sesion_id].push(q);
      return acc;
    }, {});

    const channelStats = sessions.reduce((acc, session) => {
      const channel = session.canal || 'unknown';
      if (!acc[channel]) {
        acc[channel] = { total: 0, conversions: 0 };
      }
      acc[channel].total++;
      
      // Contar conversiones (sesiones que generaron cotizaciones)
      if (quotationsBySession[session.id]) {
        acc[channel].conversions++;
      }
      
      return acc;
    }, {});

    const totalConversions = Object.values(channelStats).reduce((sum, stats) => sum + stats.conversions, 0);
    const conversionRate = sessions.length > 0 ? (totalConversions / sessions.length) * 100 : 0;

    return {
      summary: {
        totalSessions: sessions.length,
        conversions: totalConversions,
        conversionRate: Math.round(conversionRate * 100) / 100,
        avgDuration: 0 // Placeholder - se puede calcular si hay datos de duración
      },
      channelStats,
      sessions: sessions.map(session => ({
        ...session,
        hasQuotation: !!quotationsBySession[session.id]
      })),
      chartData: this.generateSessionsChartData(sessions)
    };
  }

  processQuotationsData(quotations) {
    const statusCounts = quotations.reduce((acc, q) => {
      const status = q.estado || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const totalValue = quotations.reduce((sum, q) => sum + (q.precio_total_uf || 0), 0);
    const avgValue = quotations.length > 0 ? totalValue / quotations.length : 0;

    return {
      summary: {
        totalQuotations: quotations.length,
        totalValue: Math.round(totalValue * 100) / 100,
        avgValue: Math.round(avgValue * 100) / 100,
        statusCounts
      },
      quotations,
      chartData: this.generateQuotationsChartData(quotations)
    };
  }

  processContractsData(contracts) {
    const statusCounts = contracts.reduce((acc, c) => {
      const status = c.estado || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const totalValue = contracts.reduce((sum, c) => {
      const quotationValue = c.cotizaciones?.precio_total_uf || 0;
      return sum + quotationValue;
    }, 0);
    const avgValue = contracts.length > 0 ? totalValue / contracts.length : 0;

    return {
      summary: {
        totalContracts: contracts.length,
        totalValue: Math.round(totalValue * 100) / 100,
        avgValue: Math.round(avgValue * 100) / 100,
        statusCounts
      },
      contracts,
      chartData: this.generateContractsChartData(contracts)
    };
  }

  processCertificatesData(certificates) {
    const now = new Date();
    const statusCounts = certificates.reduce((acc, c) => {
      const status = c.estado || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const validCertificates = certificates.filter(c => 
      c.fecha_vencimiento && new Date(c.fecha_vencimiento) > now
    ).length;

    const expiredCertificates = certificates.filter(c => 
      c.fecha_vencimiento && new Date(c.fecha_vencimiento) <= now
    ).length;

    return {
      summary: {
        totalCertificates: certificates.length,
        validCertificates,
        expiredCertificates,
        statusCounts
      },
      certificates,
      chartData: this.generateCertificatesChartData(certificates)
    };
  }

  processSubscriptionsData(subscriptions) {
    const statusCounts = subscriptions.reduce((acc, s) => {
      const status = s.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const activeSubscriptions = subscriptions.filter(s => s.status === 'active').length;
    const totalRevenue = subscriptions.reduce((sum, s) => sum + (s.amount || 0), 0);
    const avgRevenue = subscriptions.length > 0 ? totalRevenue / subscriptions.length : 0;

    return {
      summary: {
        totalSubscriptions: subscriptions.length,
        activeSubscriptions,
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        avgRevenue: Math.round(avgRevenue * 100) / 100,
        statusCounts
      },
      subscriptions,
      chartData: this.generateSubscriptionsChartData(subscriptions)
    };
  }

  // Generar datos para gráficos
  generateSessionsChartData(sessions) {
    const dailyData = sessions.reduce((acc, session) => {
      const date = new Date(session.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0 };
      }
      acc[date].count++;
      return acc;
    }, {});

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  generateQuotationsChartData(quotations) {
    const dailyData = quotations.reduce((acc, quotation) => {
      const date = new Date(quotation.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0, value: 0 };
      }
      acc[date].count++;
      acc[date].value += quotation.precio_total_uf || 0;
      return acc;
    }, {});

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  generateContractsChartData(contracts) {
    const dailyData = contracts.reduce((acc, contract) => {
      const date = new Date(contract.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0, value: 0 };
      }
      acc[date].count++;
      acc[date].value += contract.cotizaciones?.precio_total_uf || 0;
      return acc;
    }, {});

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  generateCertificatesChartData(certificates) {
    const dailyData = certificates.reduce((acc, certificate) => {
      const date = new Date(certificate.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0 };
      }
      acc[date].count++;
      return acc;
    }, {});

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  generateSubscriptionsChartData(subscriptions) {
    const dailyData = subscriptions.reduce((acc, subscription) => {
      const date = new Date(subscription.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, count: 0, revenue: 0 };
      }
      acc[date].count++;
      acc[date].revenue += subscription.amount || 0;
      return acc;
    }, {});

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  // Datos por defecto en caso de error
  getDefaultSessionsData() {
    return {
      summary: { totalSessions: 0, conversions: 0, conversionRate: 0, avgDuration: 0 },
      channelStats: {},
      sessions: [],
      chartData: []
    };
  }

  getDefaultQuotationsData() {
    return {
      summary: { totalQuotations: 0, totalValue: 0, avgValue: 0, statusCounts: {} },
      quotations: [],
      chartData: []
    };
  }

  getDefaultContractsData() {
    return {
      summary: { totalContracts: 0, totalValue: 0, avgValue: 0, statusCounts: {} },
      contracts: [],
      chartData: []
    };
  }

  getDefaultCertificatesData() {
    return {
      summary: { totalCertificates: 0, validCertificates: 0, expiredCertificates: 0, statusCounts: {} },
      certificates: [],
      chartData: []
    };
  }

  getDefaultSubscriptionsData() {
    return {
      summary: { totalSubscriptions: 0, activeSubscriptions: 0, totalRevenue: 0, avgRevenue: 0, statusCounts: {} },
      subscriptions: [],
      chartData: []
    };
  }
}

export default DetailedAnalyticsService;